import React, { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import styles from './tooltip.module.css';

const GAP = 8;

const getPosition = (rect, placement) => {
  switch (placement) {
    case 'bottom':
      return {
        top: rect.bottom + GAP + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2 + window.scrollY,
        left: rect.left - GAP + window.scrollX
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2 + window.scrollY,
        left: rect.right + GAP + window.scrollX
      };
    case 'top':
    default:
      return {
        top: rect.top - GAP + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX
      };
  }
};

/**
 * Tooltip
 *
 * Props:
 *   content   — string | ReactNode  Text shown in the tooltip bubble.
 *   placement — 'top' | 'bottom' | 'left' | 'right'  (default: 'top')
 *   children  — the trigger element.
 *
 * Usage:
 *   <Tooltip content="Helpful hint">
 *     <button>Hover me</button>
 *   </Tooltip>
 */
export const Tooltip = ({ content, placement = 'top', children }) => {
  const id = useId();
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);

  const show = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos(getPosition(rect, placement));
  };

  const hide = () => setPos(null);

  return (
    <span
      ref={triggerRef}
      className={styles.wrapper}
      aria-describedby={pos ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            className={cx(styles.bubble, styles[placement])}
            style={{ top: pos.top, left: pos.left }}
          >
            {content}
            <span className={cx(styles.arrow, styles[`arrow_${placement}`])} aria-hidden />
          </span>,
          document.body
        )}
    </span>
  );
};
